import type { Node } from '@xyflow/react'
import {
  ENTITY_NODE_HEIGHT,
  ENTITY_NODE_WIDTH,
  getMemoryNodeHeight,
  MEMORY_NODE_HEIGHT,
  MEMORY_NODE_WIDTH,
  type EntityNodeData,
  type MemoryNodeData,
} from '@/utils/graph-data'

export type GraphNode = Node<MemoryNodeData | EntityNodeData>

export function getNodeDimensions(node: GraphNode) {
  if (node.width && node.height) {
    return { width: node.width, height: node.height }
  }
  if (node.type === 'entity') {
    return { width: ENTITY_NODE_WIDTH, height: ENTITY_NODE_HEIGHT }
  }
  const memory = node.data as MemoryNodeData | undefined
  return {
    width: MEMORY_NODE_WIDTH,
    height: memory?.memory ? getMemoryNodeHeight(memory.memory) : MEMORY_NODE_HEIGHT,
  }
}

export function withNodeDimensions(nodes: GraphNode[]): GraphNode[] {
  return nodes.map((node) => {
    const { width, height } = getNodeDimensions(node)
    return { ...node, width, height }
  })
}

type CollisionBox = {
  x: number
  y: number
  width: number
  height: number
  moved: boolean
  node: GraphNode
}

/**
 * Naive AABB collision resolver (xyflow node-collision-algorithms pattern).
 * @see https://reactflow.dev/examples/layout/node-collisions
 */
export function resolveCollisions(
  nodes: GraphNode[],
  {
    iterations = 120,
    overlapThreshold = 0.5,
    margin = 18,
  }: {
    iterations?: number
    overlapThreshold?: number
    margin?: number
  } = {},
): GraphNode[] {
  const boxes: CollisionBox[] = nodes.map((node) => {
    const { width, height } = getNodeDimensions(node)
    return {
      x: node.position.x - margin,
      y: node.position.y - margin,
      width: width + margin * 2,
      height: height + margin * 2,
      node,
      moved: false,
    }
  })

  for (let iter = 0; iter <= iterations; iter += 1) {
    let moved = false

    for (let i = 0; i < boxes.length; i += 1) {
      for (let j = i + 1; j < boxes.length; j += 1) {
        const a = boxes[i]
        const b = boxes[j]

        const centerAX = a.x + a.width * 0.5
        const centerAY = a.y + a.height * 0.5
        const centerBX = b.x + b.width * 0.5
        const centerBY = b.y + b.height * 0.5

        const dx = centerAX - centerBX
        const dy = centerAY - centerBY
        const px = (a.width + b.width) * 0.5 - Math.abs(dx)
        const py = (a.height + b.height) * 0.5 - Math.abs(dy)

        if (px > overlapThreshold && py > overlapThreshold) {
          a.moved = true
          b.moved = true
          moved = true

          if (px < py) {
            const sx = dx > 0 ? 1 : -1
            const moveAmount = (px / 2) * sx
            a.x += moveAmount
            b.x -= moveAmount
          } else {
            const sy = dy > 0 ? 1 : -1
            const moveAmount = (py / 2) * sy
            a.y += moveAmount
            b.y -= moveAmount
          }
        }
      }
    }

    if (!moved) break
  }

  return boxes.map((box) => {
    if (!box.moved) return box.node
    return {
      ...box.node,
      position: {
        x: box.x + margin,
        y: box.y + margin,
      },
    }
  })
}
